import { useQuery } from "@tanstack/react-query";
import {
  LoaderCircle,
  SlidersHorizontal
} from "lucide-react";
import {
  useSearchParams
} from "react-router";

import {
  ShopByCategorySection
} from "../components/category/ShopByCategorySection";
import { ProductCard } from "../components/product/ProductCard";
import {
  getCategories,
  getProducts
} from "../services/catalog";

export function ShopPage() {
  const [
    searchParams,
    setSearchParams
  ] = useSearchParams();

  const selectedCategory =
    searchParams.get("category") ?? "";

  function selectCategory(
    categorySlug: string
  ) {
    if (categorySlug) {
      setSearchParams({
        category: categorySlug
      });
    } else {
      setSearchParams({});
    }
  }

  const categoriesQuery = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories
  });

  const productsQuery = useQuery({
    queryKey: [
      "products",
      selectedCategory
    ],
    queryFn: () =>
      getProducts(
        selectedCategory || undefined
      )
  });

  return (
    <main>
      <section className="bg-[#efe5d8] py-20">
        <div className="rini-container text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            The RiNi collection
          </p>

          <h1 className="rini-heading mt-4 text-5xl font-semibold sm:text-6xl">
            Shop bodycare made with thought and care
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-[#746d65]">
            Explore handmade soaps, haircare, herbal hair
            colours, lip care and thoughtfully arranged gift
            hampers.
          </p>
        </div>
      </section>

      {categoriesQuery.data && (
        <ShopByCategorySection
          categories={categoriesQuery.data}
          compact
        />
      )}

      <section className="rini-section">
        <div className="rini-container">
          <div className="flex flex-col gap-6 border-b border-[#5e3b2c]/10 pb-7 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  selectCategory("")
                }
                className={[
                  "rounded-full px-5 py-2.5 text-sm font-semibold transition",
                  selectedCategory === ""
                    ? "bg-[#5e3b2c] text-white"
                    : "border border-[#5e3b2c]/20 text-[#5e3b2c]"
                ].join(" ")}
              >
                All products
              </button>

              {categoriesQuery.data
                ?.filter(
                  (category) =>
                    category.isActive &&
                    category.slug !==
                      "test-product"
                )
                .sort(
                  (left, right) =>
                    left.displayOrder -
                    right.displayOrder
                )
                .map(
                (category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() =>
                      selectCategory(
                        category.slug
                      )
                    }
                    className={[
                      "rounded-full px-5 py-2.5 text-sm font-semibold transition",
                      selectedCategory ===
                      category.slug
                        ? "bg-[#5e3b2c] text-white"
                        : "border border-[#5e3b2c]/20 text-[#5e3b2c]"
                    ].join(" ")}
                  >
                    {category.name}
                  </button>
                )
              )}
            </div>

            <button
              type="button"
              className="inline-flex items-center gap-2 self-start rounded-full border border-[#5e3b2c]/20 px-5 py-2.5 text-sm font-semibold text-[#5e3b2c]"
            >
              <SlidersHorizontal size={16} />
              Filters
            </button>
          </div>

          {productsQuery.isLoading && (
            <div className="flex min-h-80 items-center justify-center">
              <LoaderCircle
                className="animate-spin text-[#70845b]"
                size={34}
              />
            </div>
          )}

          {productsQuery.isError && (
            <div className="my-12 rounded-3xl bg-[#f7e6e2] p-8 text-center text-[#713f36]">
              Products could not be loaded. Please try again.
            </div>
          )}

          {productsQuery.data && (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {productsQuery.data.map(
                (product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                  />
                )
              )}
            </div>
          )}

          {productsQuery.data?.length === 0 && (
            <div className="py-20 text-center">
              <h2 className="rini-heading text-3xl">
                No products found
              </h2>

              <p className="mt-3 text-[#746d65]">
                Products will be added to this collection soon.
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
